import { useShowNotification } from '@inflearn/ds-react';
import {
  CertificationModal,
  CertificationNudgeModal,
  CertificationStatusModal,
  IntegratedCertificationModalsProvider,
  useCertificationStatus,
} from '@inflearn/integrated-certification-modals';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash-es';
import { useEffect } from 'react';

import { STATUS } from './constant';
import { API_ENDPOINT, useV2SessionQuery } from '../../@types/uccSchema';
import { eventNames } from '../../constants/gtmEventNames';
import { EVENT_ID } from '../../constants/MODAL_EVENT_ID';
import { useForAntManModalDisclosure } from '../../hooks/useForAntManModalDisclosure';
import { useTrackEvent } from '../../utils/useTrackEvent';

export default function IntegratedCertificationModal() {
  const queryClient = useQueryClient();
  const [isFirstModalOpened, { close: closeFirstModal }, eventProps] = useForAntManModalDisclosure({
    eventId: EVENT_ID.INTEGRATED_CERTIFICATION_MODAL,
  });
  const [
    isSimpleCertificationModalOpened,
    { open: openCertificationModal, close: closeCertificationModal },
  ] = useDisclosure(false);
  const { showNotification } = useShowNotification();

  const { data: certificationData, isError: isCertificationStatusError } = useCertificationStatus();
  const { data: session } = useV2SessionQuery({
    queryKey: [API_ENDPOINT.V2_SESSION],
    url: API_ENDPOINT.V2_SESSION,
  });

  const { trackEvent } = useTrackEvent();

  const userName = session?.userName || '학습자';

  useEffect(() => {
    if (isCertificationStatusError || !isFirstModalOpened) {
      return;
    }

    trackEvent(eventNames.VIEW_CERT_INTEGRATED_MODAL, {
      user_name: userName,
      click_spot: 'sdp_merovingian',
      course_identification: eventProps?.courseId || null,
      unit_id: eventProps?.unitId,
      course_title: eventProps?.courseTitle,
    });
  }, [isFirstModalOpened]);

  if (isNil(certificationData) || isNil(session)) {
    return null;
  }

  if (isCertificationStatusError) {
    showNotification({
      type: 'error',
      title: '소속인증 조회에 실패했습니다.',
    });
  }

  const status = certificationData.status;

  // NO_AUTH -> 소속인증안내 모달
  const isNudgeModalOpened = isFirstModalOpened && status === STATUS.NO_AUTH;
  // 아래 두 상황에 대한 판단은 CertificationStatusModal 내부에서 처리됨
  // 1) 경력없음 표시 -> CAREER && 경력 데이터 X 일 때
  // 2) 소속인증완료 표시 -> INSTITUTION || (CAREER && 경력 데이터 O) || (ALL && 경력 데이터 O) 일 때
  const isStatusModalOpened = isFirstModalOpened && status !== STATUS.NO_AUTH;

  const handleOpenCertificationModal = () => {
    closeFirstModal();
    openCertificationModal();
  };

  return (
    <IntegratedCertificationModalsProvider service="inflearn">
      <CertificationNudgeModal
        opened={isNudgeModalOpened}
        onClose={closeFirstModal}
        onNext={handleOpenCertificationModal}
        userName={userName}
      />
      <CertificationStatusModal
        opened={isStatusModalOpened}
        onClose={closeFirstModal}
        onClickReCertification={handleOpenCertificationModal}
        onClickMailCertification={handleOpenCertificationModal}
      />
      <CertificationModal
        opened={isSimpleCertificationModalOpened}
        onClose={closeCertificationModal}
        onComplete={() => void queryClient.invalidateQueries(useCertificationStatus.queryKey)}
      />
    </IntegratedCertificationModalsProvider>
  );
}
